/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { forwardRef } from 'react';
import i18n from 'i18next';

import PropTypes from 'prop-types';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import LockIcon from '@mui/icons-material/Lock';

import { Label } from 'uikit/labels';
import { Typo } from 'uikit/content';
import { CardMedia, CardSkeleton } from 'uikit/cards';

import styles from '../styles/uikitCardsCard.module.scss';

const Card = forwardRef(
  (
    {
      children = null,
      className = '',
      dateMessage = '',
      duration = 0,
      element = 'div',
      id = '',
      image = null,
      isComplete = false,
      isExternalLink = false,
      isSkeletonLoading = false,
      linkTarget = '_self',
      liveText = '',
      onClick = null,
      progress = 0,
      status = '',
      statusCustomMessage = '',
      testId = '',
      to = '',
      trackingId = '',
      type = '',
      variant = 'social',
      videoType = '', // deprecated in favor of type
    },
    ref,
  ) => {
    const cardProps = {};
    let Element = element;

    if (to && isExternalLink) {
      Element = 'a';
      cardProps.href = to;
      cardProps.target = linkTarget;
    } else if (to) {
      Element = Link;
      cardProps.to = to;
    } else if (onClick) {
      Element = 'button';
      cardProps.type = 'button';
      cardProps.onClick = onClick;
    }

    // To handle any continue usage of deprecated videoType prop
    // Otherwise, we can just drop this and use type
    const usageType = type || videoType;

    return (
      <Element
        className={classnames(
          styles.element,
          {
            [styles.hasHover]: !!to || !!onClick,
            [styles.isGridContainer]: variant === 'grid',
          },
          className,
        )}
        ref={ref}
        id={id}
        data-testid={testId}
        data-object-name={trackingId}
        {...cardProps}
      >
        {variant === 'social' && children}

        {variant === 'grid' && (
          <div className={styles.isVariantGrid}>
            {isSkeletonLoading ? (
              <CardSkeleton variant="grid" />
            ) : (
              <motion.div
                className={styles.motion}
                key={id || 'key'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <CardMedia
                  dateMessage={dateMessage}
                  duration={duration}
                  folderIconClassName={styles.folder}
                  image={image}
                  isComplete={isComplete}
                  liveText={liveText}
                  progress={progress}
                  status={status}
                  statusCustomMessage={statusCustomMessage}
                  type={usageType}
                  variant="grid"
                />
                {children}
              </motion.div>
            )}
          </div>
        )}
        {(variant === 'list' || variant === 'embed') &&
          (isSkeletonLoading ? (
            <CardSkeleton variant={variant} />
          ) : (
            <motion.div
              className={styles.motion}
              key={id || 'key'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div
                className={classnames({
                  [styles.isVariantList]: variant === 'list',
                  [styles.isVariantEmbed]: variant === 'embed',
                })}
              >
                <CardMedia
                  duration={duration}
                  folderIconClassName={styles.folder}
                  image={image}
                  isComplete={isComplete}
                  progress={progress}
                  status={status}
                  statusCustomMessage={statusCustomMessage}
                  type={usageType}
                  variant={variant}
                />
                {children}
              </div>
            </motion.div>
          ))}
      </Element>
    );
  },
);

const ContentContainer = ({
  children,
  dateMessage = '',
  dateMessageType = 'default',
  isPurchaseRequired = false,
  liveText = '',
  testId = '',
  variant,
}) => {
  const getLabels = () => {
    if (!liveText && !isPurchaseRequired && !dateMessage) return null;
    if (liveText) {
      return (
        <div className={styles.topLabelsContainer}>
          <Label text={liveText} testId="live-label" type="live" />
        </div>
      );
    }
    const labels = [];
    if (isPurchaseRequired) {
      labels.push(
        <Label
          icon={<LockIcon />}
          key="purchase-required-label"
          testId="purchase-required-label"
          text={i18n.t('purchase-required', 'Purchase required')}
          type="brand"
        />,
      );
    }
    if (dateMessage) {
      labels.push(
        <Label
          key="date-message-label"
          testId="date-message-label"
          text={dateMessage}
          type={dateMessageType}
        />,
      );
    }
    return <div className={styles.topLabelsContainer}>{labels}</div>;
  };

  return (
    <div
      className={classnames(styles.contentContainer, { hasCompactMeta: variant === 'embed' })}
      data-testid={testId}
    >
      {(variant === 'embed' || variant === 'list') && getLabels()}
      {children}
    </div>
  );
};

const Content = ({ description = '', title, variant = '' }) => (
  <div className={styles.content}>
    <div className={styles.title}>
      {variant === 'embed' ? (
        <Typo element="h3" variant="b01" weight="medium">
          {title}
        </Typo>
      ) : (
        <Typo element="h3" variant="d01">
          {title}
        </Typo>
      )}
    </div>
    {variant === 'list' && (
      <div className={styles.description}>
        <Typo element="p" variant="b01">
          {description}
        </Typo>
      </div>
    )}
  </div>
);

const MetaContainer = ({ children }) => <div className={styles.metaContainer}>{children}</div>;

Card.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  dateMessage: PropTypes.string,
  duration: PropTypes.number,
  element: PropTypes.oneOf(['div', 'article', 'button', 'a']),
  id: PropTypes.string,
  image: PropTypes.string,
  isComplete: PropTypes.bool,
  isExternalLink: PropTypes.bool,
  isSkeletonLoading: PropTypes.bool,
  linkTarget: PropTypes.oneOf(['_self', '_blank', '_parent', '_top']),
  liveText: PropTypes.string,
  onClick: PropTypes.func,
  progress: PropTypes.number,
  status: PropTypes.oneOf([
    'ai',
    'completed',
    'equivalentPassed',
    'facilitated',
    'inProgress',
    'overdue',
    'recommended',
    'required',
    '',
  ]),
  statusCustomMessage: PropTypes.string,
  testId: PropTypes.string,
  to: PropTypes.string,
  trackingId: PropTypes.string,
  type: PropTypes.oneOf(['course', 'live', 'topic', 'video']), // upnext is deprecated
  variant: PropTypes.oneOf(['embed', 'grid', 'list', 'social']),
  videoType: PropTypes.oneOf(['live', 'topic', 'video']),
};

ContentContainer.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['embed', 'grid', 'list']).isRequired,
  dateMessage: PropTypes.string,
  dateMessageType: PropTypes.oneOf(['default', 'success', 'warning']),
  isPurchaseRequired: PropTypes.bool,
  liveText: PropTypes.string,
  testId: PropTypes.string,
};

Content.propTypes = {
  description: PropTypes.string,
  title: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['', 'embed', 'grid', 'list']),
};

MetaContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

Card.ContentContainer = ContentContainer;
Card.Content = Content;
Card.MetaContainer = MetaContainer;

export { Card as CardStorybook };
export default Card;
